import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext.jsx';
import {
  assignSeat,
  getCompany,
  getCompanyUsage,
  inviteMember,
  listMembers,
  removeMember,
  removeSeat,
} from '../api/accountPortalClient.js';
import { consumesSeat } from '../utils/companySeats.js';
import { phInviteSent, phInviteFailed, phSeatAssigned, phSeatAssignFailed } from '../utils/posthogEvents.js';

const PLAN_LABELS = {
  starter: 'Starter',
  solo: 'Solo',
  studio: 'Studio',
  firm: 'Firm',
};

const ROLE_LABELS = {
  ADMIN: 'Admin',
  BILLING_ADMIN: 'Billing Admin',
  MEMBER: 'Member',
};

function norm(v) {
  return String(v || '').trim().toUpperCase();
}

function roleLabel(role) {
  const key = norm(role);
  return ROLE_LABELS[key] || key || '—';
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

function formatMinutes(total) {
  const n = Math.max(0, Number(total) || 0);
  if (n < 60) return `${n} min`;
  const hours = Math.floor(n / 60);
  const mins = n % 60;
  return mins ? `${hours}h ${mins}m` : `${hours}h`;
}

export default function DashboardPage() {
  const { user, signOut, refreshUser } = useAuth();
  const membership = user?.companies?.[0] || null;
  const companyId = membership?.companyId || '';
  const [company, setCompany] = useState(null);
  const [stats, setStats] = useState(null);
  const [members, setMembers] = useState([]);
  const [usageRows, setUsageRows] = useState([]);
  const [usageDays, setUsageDays] = useState(30);
  const [loading, setLoading] = useState(true);
  const [usageLoading, setUsageLoading] = useState(false);
  const [error, setError] = useState('');

  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('MEMBER');
  const [isInviting, setIsInviting] = useState(false);
  const [inviteError, setInviteError] = useState('');
  const [inviteSuccess, setInviteSuccess] = useState('');
  const [inviteSubmitting, setInviteSubmitting] = useState(false);
  const [rowBusyId, setRowBusyId] = useState(null);

  const myRole = norm(membership?.role);
  const isAdmin = myRole === 'ADMIN';
  const canInvite = isAdmin || myRole === 'BILLING_ADMIN';
  const myEmail = String(user?.email || '').toLowerCase();
  const myMemberId = membership?.memberId || '';

  const loadCompanyData = useCallback(async (id) => {
    if (!id) return;
    setLoading(true);
    setError('');
    try {
      const [detail, memberList] = await Promise.all([getCompany(id), listMembers(id)]);
      setCompany(detail.company || null);
      setStats(detail.stats || null);
      setMembers(memberList);
    } catch (e) {
      setError(e?.message || 'Failed to load company');
      setCompany(null);
      setStats(null);
      setMembers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadUsage = useCallback(async (id, days) => {
    if (!id || !isAdmin) return;
    setUsageLoading(true);
    try {
      const data = await getCompanyUsage(id, days);
      setUsageRows(data.items || []);
      setUsageDays(data.days || days);
    } catch {
      setUsageRows([]);
    } finally {
      setUsageLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (companyId) loadCompanyData(companyId);
  }, [companyId, loadCompanyData]);

  useEffect(() => {
    if (companyId && isAdmin) loadUsage(companyId, 30);
  }, [companyId, isAdmin, loadUsage]);

  const visibleMembers = useMemo(
    () => members.filter((m) => norm(m.status) !== 'REMOVED'),
    [members]
  );

  const displayStats = useMemo(() => {
    const seatLimit = stats?.seatLimit ?? Number(company?.seatLimit || 0);
    const usedSeats = stats?.usedSeats ?? members.filter((m) => consumesSeat(m)).length;
    const availableSeats = stats?.availableSeats ?? Math.max(0, seatLimit - usedSeats);
    const planKeyRaw = String(company?.planKey || '').trim();
    const planKey = planKeyRaw ? PLAN_LABELS[planKeyRaw.toLowerCase()] || planKeyRaw : '—';
    const billing = company?.billingCycle ? String(company.billingCycle) : '';
    const planLabel = billing ? `${planKey} (${billing})` : planKey;
    return {
      seatLimit,
      usedSeats,
      availableSeats,
      subscriptionStatus: company?.subscriptionStatus || '—',
      planLabel,
    };
  }, [company, members, stats]);

  const usageByEmail = useMemo(() => {
    const map = new Map();
    for (const row of usageRows) {
      if (row.email) map.set(String(row.email).toLowerCase(), row);
    }
    return map;
  }, [usageRows]);

  async function handleInviteSubmit(e) {
    e.preventDefault();
    setInviteError('');
    setInviteSuccess('');

    const email = inviteEmail.trim().toLowerCase();
    if (!email) return setInviteError('Email is required.');
    if (!isValidEmail(email)) return setInviteError('Enter a valid email address.');
    if (displayStats.availableSeats <= 0 && inviteRole !== 'BILLING_ADMIN') {
      return setInviteError('No available seats. Increase seat limit to invite more users.');
    }

    setInviteSubmitting(true);
    try {
      await inviteMember({ companyId, email, role: inviteRole });
      await loadCompanyData(companyId);
      setInviteSuccess(`Invite sent to ${email}.`);
      phInviteSent({ role: inviteRole });
      setIsInviting(false);
      setInviteEmail('');
      setInviteRole('MEMBER');
    } catch (err) {
      setInviteError(err?.message || 'Failed to send invite.');
      phInviteFailed({ role: inviteRole, reason: err?.message });
    } finally {
      setInviteSubmitting(false);
    }
  }

  async function handleRemoveMember(member) {
    const ok = window.confirm(`Remove ${member.email} from the company?`);
    if (!ok) return;
    setRowBusyId(member.id);
    setInviteError('');
    setInviteSuccess('');
    try {
      await removeMember({ companyId, memberId: member.id });
      await loadCompanyData(companyId);
      setInviteSuccess(`Removed ${member.email}.`);
    } catch (err) {
      setInviteError(err?.message || 'Failed to remove member.');
    } finally {
      setRowBusyId(null);
    }
  }

  async function handleAssignSeat(member) {
    const label = member.email === myEmail ? 'yourself' : member.email;
    const ok = window.confirm(`Assign a product seat to ${label}?`);
    if (!ok) return;
    setRowBusyId(member.id);
    setInviteError('');
    setInviteSuccess('');
    const isSelf = member.id === myMemberId;
    try {
      const result = await assignSeat({ companyId, memberId: member.id });
      if (isSelf) await refreshUser();
      await loadCompanyData(companyId);
      setInviteSuccess(result?.message || `Seat assigned to ${member.email}.`);
      phSeatAssigned({ isSelf });
    } catch (err) {
      setInviteError(err?.message || 'Failed to assign seat.');
      phSeatAssignFailed({ isSelf, reason: err?.message });
    } finally {
      setRowBusyId(null);
    }
  }

  async function handleRemoveSeat(member) {
    const label = member.email === myEmail ? 'yourself' : member.email;
    const ok = window.confirm(
      `Remove the product seat from ${label}? They will keep billing portal access only.`
    );
    if (!ok) return;
    setRowBusyId(member.id);
    setInviteError('');
    setInviteSuccess('');
    try {
      const result = await removeSeat({ companyId, memberId: member.id });
      if (member.id === myMemberId) await refreshUser();
      await loadCompanyData(companyId);
      setInviteSuccess(result?.message || `Seat removed from ${member.email}.`);
    } catch (err) {
      setInviteError(err?.message || 'Failed to remove seat.');
    } finally {
      setRowBusyId(null);
    }
  }

  function canManageMemberRow(member) {
    if (!canInvite) return false;
    if (norm(member.status) === 'REMOVED') return false;
    if (String(member.email || '').toLowerCase() === myEmail) return false;
    return true;
  }

  function canAssignSeat(member) {
    if (norm(member.status) === 'REMOVED') return false;
    if (consumesSeat(member)) return false;
    if (norm(member.role) !== 'BILLING_ADMIN') return false;
    if (!canInvite) return false;
    const isSelf = String(member.email || '').toLowerCase() === myEmail;
    if (isSelf) return true;
    return isAdmin;
  }

  function canRemoveSeat(member) {
    if (!consumesSeat(member)) return false;
    if (!canInvite) return false;
    const isSelf = String(member.email || '').toLowerCase() === myEmail;
    if (isSelf) return true;
    return canManageMemberRow(member) || isAdmin;
  }

  if (!membership) {
    return (
      <div className="page">
        <div className="card">
          <h1 className="logo">CODiii</h1>
          <p className="subtitle">No company access found for {user?.email || 'your account'}.</p>
          <button className="btn btn-outline" type="button" onClick={signOut}>
            Sign out
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page" style={{ alignItems: 'flex-start', paddingTop: 32 }}>
      <div className="card card-wide">
        <div className="toolbar">
          <div>
            <h1 className="logo" style={{ marginBottom: 4 }}>
              License Management
            </h1>
            <p className="subtitle" style={{ margin: 0 }}>
              {company?.name || 'Company'} • Your role: <strong>{roleLabel(myRole)}</strong>
            </p>
          </div>
          <div className="toolbar">
            {canInvite && (
              <Link to="/integrations/sharepoint" className="btn btn-outline">
                Connect SharePoint
              </Link>
            )}
            <button className="btn btn-outline" type="button" onClick={signOut}>
              Sign out
            </button>
          </div>
        </div>

        {loading && <p className="subtitle">Loading…</p>}
        {!loading && error && <div className="alert alert-error">{error}</div>}
        {!loading && inviteError && <div className="alert alert-error">{inviteError}</div>}
        {!loading && inviteSuccess && <div className="alert alert-success">{inviteSuccess}</div>}

        {!loading && !error && company && (
          <>
            <div className="section">
              <h2>Seats &amp; usage</h2>
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-number">{displayStats.seatLimit}</div>
                  <div className="stat-label">Plan limit</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">{displayStats.usedSeats}</div>
                  <div className="stat-label">Used</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number">{displayStats.availableSeats}</div>
                  <div className="stat-label">Available</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number" style={{ fontSize: 18 }}>
                    {String(displayStats.subscriptionStatus)}
                  </div>
                  <div className="stat-label">Status</div>
                </div>
                <div className="stat-card">
                  <div className="stat-number" style={{ fontSize: 18 }}>
                    {displayStats.planLabel}
                  </div>
                  <div className="stat-label">Plan</div>
                </div>
              </div>
            </div>

            {canInvite && (
              <div className="section">
                <h2>Invite a team member</h2>
                {!isInviting ? (
                  <button
                    className="btn"
                    type="button"
                    onClick={() => {
                      setInviteError('');
                      setInviteSuccess('');
                      setIsInviting(true);
                    }}
                  >
                    + Invite user
                  </button>
                ) : (
                  <form onSubmit={handleInviteSubmit}>
                    <div className="form-row">
                      <input
                        type="email"
                        placeholder="Email address"
                        value={inviteEmail}
                        onChange={(e) => setInviteEmail(e.target.value)}
                        disabled={inviteSubmitting}
                      />
                      <select
                        value={inviteRole}
                        onChange={(e) => setInviteRole(e.target.value)}
                        disabled={inviteSubmitting}
                        style={{ minWidth: 180 }}
                      >
                        <option value="MEMBER">Member</option>
                        <option value="ADMIN">Admin</option>
                        <option value="BILLING_ADMIN">Billing Admin</option>
                      </select>
                    </div>
                    <div className="form-actions">
                      <button
                        className="btn btn-outline"
                        type="button"
                        onClick={() => {
                          setIsInviting(false);
                          setInviteEmail('');
                          setInviteRole('MEMBER');
                        }}
                        disabled={inviteSubmitting}
                      >
                        Cancel
                      </button>
                      <button className="btn" type="submit" disabled={inviteSubmitting || !inviteEmail.trim()}>
                        {inviteSubmitting ? 'Sending…' : 'Send invite'}
                      </button>
                    </div>
                    <p style={{ fontSize: 12, color: 'var(--muted)', marginTop: 8 }}>
                      Seats available: <strong>{displayStats.availableSeats}</strong>
                      {inviteRole === 'BILLING_ADMIN' && ' (billing admins do not consume a seat until assigned)'}
                    </p>
                  </form>
                )}
              </div>
            )}

            <div className="section">
              <h2>{canInvite ? 'Team members' : 'Team members (read-only)'}</h2>
              <div className="member-list">
                {visibleMembers.map((member) => {
                  const busy = rowBusyId === member.id;
                  const memberRoleLabel = roleLabel(member.role);
                  const statusLabel = norm(member.status) || '—';
                  const hasSeat = consumesSeat(member);
                  const isSelf = String(member.email || '').toLowerCase() === myEmail;

                  return (
                    <div className="member-row" key={member.id}>
                      <div>
                        <div className="member-email">
                          {member.email || '—'}
                          {isSelf && (
                            <span style={{ color: 'var(--muted)', fontWeight: 500 }}> (you)</span>
                          )}
                        </div>
                        <div className="member-meta">
                          Role: {memberRoleLabel} • Status: {statusLabel} •{' '}
                          {hasSeat ? 'Has product seat' : 'No product seat'}
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <span className="badge">{memberRoleLabel}</span>
                        <span className="badge">{hasSeat ? 'Seat' : 'No seat'}</span>
                        {canAssignSeat(member) && (
                          <button
                            className="btn"
                            type="button"
                            disabled={busy || displayStats.availableSeats <= 0}
                            onClick={() => handleAssignSeat(member)}
                          >
                            {busy ? 'Saving…' : 'Assign seat'}
                          </button>
                        )}
                        {canRemoveSeat(member) && (
                          <button
                            className="btn btn-outline"
                            type="button"
                            disabled={busy}
                            onClick={() => handleRemoveSeat(member)}
                          >
                            {busy ? 'Saving…' : 'Remove seat'}
                          </button>
                        )}
                        {canManageMemberRow(member) && (
                          <button
                            className="btn btn-outline"
                            type="button"
                            disabled={busy}
                            onClick={() => handleRemoveMember(member)}
                          >
                            Remove
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })}
                {!visibleMembers.length && <div className="member-row">No members yet.</div>}
              </div>
            </div>

            {isAdmin && (
              <div className="section">
                <h2>Product usage</h2>
                <p className="subtitle" style={{ marginTop: 0 }}>
                  Active time per employee over the last {usageDays} days — updated as users work in the
                  product app.
                </p>
                {usageLoading && <p className="subtitle">Loading usage…</p>}
                {!usageLoading && (
                  <div className="usage-table-wrap">
                    <table className="usage-table">
                      <thead>
                        <tr>
                          <th>Employee</th>
                          <th>Active time</th>
                          <th>Active days</th>
                        </tr>
                      </thead>
                      <tbody>
                        {visibleMembers
                          .filter((m) => norm(m.role) !== 'BILLING_ADMIN' || consumesSeat(m))
                          .map((member) => {
                            const usage = usageByEmail.get(String(member.email || '').toLowerCase());
                            return (
                              <tr key={member.id}>
                                <td>{member.email}</td>
                                <td>{usage ? formatMinutes(usage.minutes) : '—'}</td>
                                <td>{usage?.activeDays ?? '—'}</td>
                              </tr>
                            );
                          })}
                        {usageRows
                          .filter(
                            (row) =>
                              !visibleMembers.some(
                                (m) =>
                                  String(m.email || '').toLowerCase() === String(row.email || '').toLowerCase()
                              )
                          )
                          .map((row) => (
                            <tr key={row.userId}>
                              <td>{row.email || row.userId}</td>
                              <td>{formatMinutes(row.minutes)}</td>
                              <td>{row.activeDays}</td>
                            </tr>
                          ))}
                        {!usageRows.length && (
                          <tr>
                            <td colSpan={3} style={{ color: 'var(--muted)' }}>
                              No usage recorded yet. Time is tracked when employees use app.codiii.com.
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
